import { validateDocumentComponents, validateSectionComponents } from './components.mjs';
import { SAJU_CHART_IMAGES } from './saju-studio-charts.mjs';
import { SAJU_PRODUCT_EXAMPLES, SAJU_PRODUCT_EXAMPLE_PREFIX, createSajuProductDocument } from './template-saju-document.mjs';
import { EDITOR_SAMPLE_PRESETS, getEditorSample } from './editor-samples.mjs';

const ASSET_ROOT = '/images/content-templates/editorial';

// The catalog describes what is actually visible. Agents use these fields when
// selecting artwork, so an attractive but unrelated icon is never a topic match.
const wealthUse = ['사주 재물운 해설의 장식 아이콘', '네이비·금색 편집 템플릿의 시각적 강조'];
const wealthAvoid = ['수학·과학 개념의 증거 그림', '금액·수익률을 나타내는 데이터 차트'];
const characterUse = ['사주 성향 해설의 장식 아이콘', '네이비·금색 편집 템플릿의 시각적 강조'];
const characterAvoid = ['수학·과학 개념의 증거 그림', '실제 심리 진단 결과 표현'];
const frameAvoid = ['이 프레임만으로 개념이나 정답을 설명', '텍스트를 덮어 읽기 어렵게 배치'];
const assetDetails = {
  coins: { description: '투명 배경에 금색 동전 더미와 앞에 놓인 꽃무늬 동전 한 개.', topic: '재물의 축적', concepts: ['재물', '축적'], keywords: ['동전', '돈', 'gold', 'wealth'] },
  chart: { description: '투명 배경에 금색 상승 막대와 위쪽으로 오르는 화살표.', topic: '가치의 상승', concepts: ['변화', '증가'], keywords: ['차트', '상승', '화살표', 'wealth'] },
  book: { description: '투명 배경에 금색 장식이 있는 책 아이콘.', topic: '재물에 대한 이해', concepts: ['이해', '학습'], keywords: ['책', '독서', '지식', 'wealth'] },
  shield: { description: '투명 배경에 금색 방패와 확인 표시.', topic: '재물 관리의 기준', concepts: ['기준', '보호'], keywords: ['방패', '확인', '보호', 'wealth'] },
  wallet: { description: '투명 배경에 금색 장식의 지갑 아이콘.', topic: '재물의 결과', concepts: ['지갑', '재물'], keywords: ['지갑', '돈', 'wealth'] },
  repeat: { description: '투명 배경에 순환하는 금색 화살표 모양.', topic: '재물 관리의 반복', concepts: ['반복', '순환'], keywords: ['반복', '순환', '화살표', 'wealth'] },
  'shield-alert': { description: '투명 배경에 주의를 나타내는 금색 방패 아이콘.', topic: '재물 관리의 확인', concepts: ['주의', '점검'], keywords: ['방패', '경고', '확인', 'wealth'] },
  eye: { description: '금색 햇살 장식에 둘러싸인 파란 홍채의 눈.', topic: '성향 관찰', concepts: ['관찰', '시선'], keywords: ['눈', '관찰', '성향', 'personality'] },
  sprout: { description: '투명 배경에 금색 새싹 모티프.', topic: '성향의 성장', concepts: ['성장', '새싹'], keywords: ['새싹', '성장', 'personality'] },
  target: { description: '투명 배경에 금색 과녁 모티프.', topic: '목표와 방향', concepts: ['목표', '방향'], keywords: ['과녁', '목표', 'personality'] },
  thought: { description: '투명 배경에 금색 생각 구름 모티프.', topic: '생각과 성찰', concepts: ['생각', '성찰'], keywords: ['생각', '구름', 'personality'] },
  mountain: { description: '투명 배경에 금색 산 모티프.', topic: '성장의 과정', concepts: ['과정', '도전'], keywords: ['산', '과정', 'personality'] },
  'heart-lock': { description: '투명 배경에 금색 하트와 자물쇠 모티프.', topic: '관계와 마음', concepts: ['관계', '신뢰'], keywords: ['하트', '자물쇠', '관계', 'personality'] },
  'wealth-vignette': { description: '투명 배경의 긴 가로 장면. 동전과 복주머니, 상승 차트, 지갑을 금색·네이비 장식으로 연결.', topic: '재물 흐름 장식', concepts: ['재물', '상승', '결과'], keywords: ['재물운', '동전', '차트', '지갑', 'footer'], dimensions: { width: 1600, height: 533 } },
  'study-scene': { description: '짙은 남색 배경 위에 펼친 금색 책과 만년필, 확성기가 놓인 정사각형 장면.', subject: '학습', topic: '학습과 발표', concepts: ['읽기', '쓰기', '발표'], recommendedUses: ['학습 자료 도입 이미지', '읽기·쓰기·발표 주제의 배경 장면'], avoidUses: ['분수 계산의 정확한 도해', '시험 점수나 성적 데이터의 증거'], keywords: ['책', '만년필', '확성기', '공부', 'study'], dimensions: { width: 1024, height: 1024 } },
  'ivory-hanji': { description: '밝은 아이보리색 한지 질감이 전체에 고르게 깔린 정사각형 배경. 글자나 그림은 없다.', subject: '디자인', topic: '종이 질감', concepts: ['배경', '질감'], recommendedUses: ['자료 페이지의 은은한 종이 배경', '카드와 프레임 아래의 질감 레이어'], avoidUses: ['개념 설명의 주제 그림', '작은 아이콘으로 사용'], keywords: ['한지', '아이보리', '종이', '배경', 'texture'], dimensions: { width: 1254, height: 1254 }, format: 'image/webp' },
  'learning-paper-grid': { description: '미색 종이에 아주 연한 격자선과 드문드문 청록색 점이 있는 정사각형 반복 배경.', subject: '학습', topic: '학습지 배경', concepts: ['배경', '격자'], recommendedUses: ['학습 자료의 전체 배경', '반복되는 노트 종이 질감'], avoidUses: ['문제의 정답 도해', '작은 아이콘으로 사용'], keywords: ['격자', '학습지', '노트', '종이', '배경'], dimensions: { width: 650, height: 650 }, format: 'image/webp' },
  'learning-card-frame': { description: '흰색 가운데에 얇은 청록색 이중 테두리와 둥근 모서리가 있는 투명 배경 카드 프레임.', subject: '학습', topic: '설명 카드 프레임', concepts: ['카드', '레이아웃'], recommendedUses: ['짧은 설명과 활동 내용의 배경 카드', '문서·캔버스에 넣고 그 위에 글 배치'], avoidUses: frameAvoid, keywords: ['카드', '테두리', '프레임', '청록'], dimensions: { width: 256, height: 256 }, format: 'image/webp' },
  'learning-section-plaque': { description: '밝은 민트색 가로 판에 얇은 청록색 이중 테두리와 둥근 모서리가 있는 투명 배경 소제목 프레임.', subject: '학습', topic: '소제목 프레임', concepts: ['소제목', '레이아웃'], recommendedUses: ['학습 자료의 장 제목 배경', '문서·캔버스에 넣고 그 위에 짧은 제목 배치'], avoidUses: frameAvoid, keywords: ['제목', '라벨', '프레임', '민트'], dimensions: { width: 320, height: 120 }, format: 'image/webp' },
  'learning-summary-frame': { description: '크림색 가운데에 얇은 황금색 이중 테두리와 둥근 모서리가 있는 투명 배경 요약 카드 프레임.', subject: '학습', topic: '요약 프레임', concepts: ['요약', '레이아웃'], recommendedUses: ['수업 핵심 문장이나 정리 내용의 배경', '문서·캔버스에 넣고 그 위에 요약 문장 배치'], avoidUses: frameAvoid, keywords: ['요약', '정리', '프레임', '크림'], dimensions: { width: 256, height: 256 }, format: 'image/webp' },
  'saju-page-background': { description: '짙은 남색 세로 페이지에 금색 테두리, 별자리, 초승달, 구름 장식이 배치된 배경.', subject: '사주', topic: '사주 페이지 배경', concepts: ['배경', '페이지'], recommendedUses: ['사주 해설 페이지의 전체 배경', '네이비·금색 편집 자료의 장식'], avoidUses: ['학습 문제의 개념 도해', '텍스트와 대비가 부족한 상태로 배치'], keywords: ['별자리', '초승달', '구름', '남색', '배경'], dimensions: { width: 1055, height: 1491 } },
  'personality-card-frame': { description: '아이보리색 세로 카드에 짙은 남색과 금색 테두리, 아래쪽 작은 금색 별 장식이 있는 투명 배경 프레임.', subject: '사주', topic: '성향 설명 카드 프레임', concepts: ['카드', '성향'], recommendedUses: ['사주 성향 해설의 세로 카드 배경', '문서·캔버스에 넣고 그 위에 글 배치'], avoidUses: frameAvoid, keywords: ['성향', '카드', '아이보리', '금색'], dimensions: { width: 380, height: 523 } },
  'personality-section-frame': { description: '짙은 남색 가운데와 금색 다중 테두리, 양끝 뾰족한 장식이 있는 가로 제목판.', subject: '사주', topic: '성향 소제목 프레임', concepts: ['소제목', '성향'], recommendedUses: ['사주 성향 해설의 장 제목 배경', '문서·캔버스에 넣고 그 위에 제목 배치'], avoidUses: frameAvoid, keywords: ['성향', '제목', '남색', '금색'], dimensions: { width: 918, height: 141 } },
  'personality-summary-frame': { description: '아이보리색 긴 가로 판에 남색·금색 테두리와 양쪽 금색 햇살 무늬가 있는 요약 프레임.', subject: '사주', topic: '성향 요약 프레임', concepts: ['요약', '성향'], recommendedUses: ['사주 성향 해설의 한 문장 요약 배경', '문서·캔버스에 넣고 그 위에 글 배치'], avoidUses: frameAvoid, keywords: ['성향', '요약', '햇살', '금색'], dimensions: { width: 1500, height: 292 } },
  'wealth-card-frame': { description: '아이보리색 세로 카드에 남색과 금색 테두리, 모서리 금색 장식이 있는 투명 배경 프레임.', subject: '사주', topic: '재물운 설명 카드 프레임', concepts: ['카드', '재물운'], recommendedUses: ['사주 재물운 해설의 세로 카드 배경', '문서·캔버스에 넣고 그 위에 글 배치'], avoidUses: frameAvoid, keywords: ['재물운', '카드', '아이보리', '금색'], dimensions: { width: 432, height: 628 } },
  'wealth-section-frame': { description: '짙은 남색 가로 판에 여러 겹 금색 테두리와 양끝 뾰족한 장식이 있는 제목 프레임.', subject: '사주', topic: '재물운 소제목 프레임', concepts: ['소제목', '재물운'], recommendedUses: ['사주 재물운 해설의 장 제목 배경', '문서·캔버스에 넣고 그 위에 제목 배치'], avoidUses: frameAvoid, keywords: ['재물운', '제목', '남색', '금색'], dimensions: { width: 754, height: 178 } },
  'wealth-summary-frame': { description: '넓은 아이보리색 가로 판에 남색·금색 테두리와 양옆 금색 광선 장식이 있는 요약 프레임.', subject: '사주', topic: '재물운 요약 프레임', concepts: ['요약', '재물운'], recommendedUses: ['사주 재물운 해설의 한 문장 요약 배경', '문서·캔버스에 넣고 그 위에 글 배치'], avoidUses: frameAvoid, keywords: ['재물운', '요약', '광선', '금색'], dimensions: { width: 1050, height: 312 } },
  'fraction-tiles-hands': { description: '투명 배경에 어린이의 양손이 빨강·노랑·청록·남색 분수 원 조각을 맞추는 장면. 위쪽에 네 칸 색 타일 세 줄, 오른쪽에 빈 노트와 연필이 있다. 조각에 수치 라벨은 없다.', subject: '수학', topic: '분수의 크기와 같은 크기 나누기', concepts: ['분수', '전체와 부분', '동치분수', '조작 활동'], recommendedUses: ['분수 개념 도입', '직접 조각을 맞추는 활동 안내', '수학 수업의 장식 일러스트'], avoidUses: ['특정 분수식의 계산 결과나 정답으로 사용', '1/2 + 1/3 등 서로 다른 분모의 합을 정확히 증명하는 도해'], keywords: ['분수', '타일', '원 조각', '손', '노트', '연필', 'fraction', 'manipulative'], dimensions: { width: 1536, height: 1024 }, format: 'image/webp', provenance: { kind: 'generated', source: 'EduSense 분수 수업을 위한 OpenAI 이미지 생성', date: '2026-09-23' } },
  'fraction-six-equal-parts': { description: '같은 크기의 긴 막대를 여섯 칸으로 똑같이 나누고 한 칸만 청록색으로 채운 투명 배경 도해. 글자와 숫자는 없다.', subject: '수학', topic: '분모와 6등분', concepts: ['분모', '전체와 부분', '1/6'], recommendedUses: ['분모 6이 같은 전체를 여섯 등분한다는 설명', '같은 크기 조각을 세는 연습'], avoidUses: ['서로 다른 크기의 전체를 비교하는 근거', '분수의 덧셈 결과 전체로 사용'], keywords: ['분수', '6등분', '분모', '막대', '한 칸', 'sixths'], dimensions: { width: 1920, height: 840 } },
  'fraction-three-of-six': { description: '같은 크기의 긴 막대를 여섯 칸으로 똑같이 나누고 그중 세 칸을 산호색으로 채운 투명 배경 도해. 글자와 숫자는 없다.', subject: '수학', topic: '분자와 3/6', concepts: ['분자', '전체와 부분', '3/6', '1/2'], recommendedUses: ['여섯 조각 중 세 조각을 뜻하는 분자 설명', '3/6 시각화'], avoidUses: ['2/6 또는 5/6의 그림으로 사용', '다른 크기의 전체와 수치 비교'], keywords: ['분수', '분자', '3/6', '절반', '막대', 'sixths'], dimensions: { width: 1920, height: 840 } },
  'fraction-half-three-sixths': { description: '같은 길이의 위아래 막대에서 위는 절반, 아래는 여섯 칸 중 세 칸을 색으로 채워 같은 양임을 보여 주는 투명 배경 도해. 글자와 숫자는 없다.', subject: '수학', topic: '1/2과 3/6의 동치', concepts: ['동치분수', '통분', '1/2', '3/6'], recommendedUses: ['1/2 = 3/6을 시각적으로 설명', '분할 수가 달라도 채워진 양이 같음을 비교'], avoidUses: ['1/3 = 3/6의 근거로 사용', '전체 크기가 다른 문제의 답으로 사용'], keywords: ['분수', '통분', '동치분수', '1/2', '3/6', '막대'], dimensions: { width: 1920, height: 840 } },
  'fraction-three-plus-two-sixths': { description: '같은 길이의 여섯 칸 막대 세 개. 위 막대 세 칸은 청록색, 가운데 막대 두 칸은 산호색, 아래 막대 다섯 칸은 두 색을 합쳐 채운 투명 배경 도해. 글자와 숫자는 없다.', subject: '수학', topic: '3/6 + 2/6 = 5/6', concepts: ['같은 분모의 덧셈', '3/6', '2/6', '5/6'], recommendedUses: ['분모가 같을 때 조각 수를 더하는 설명', '1/2 + 1/3을 3/6 + 2/6으로 바꾼 다음 결과 시각화'], avoidUses: ['통분하지 않은 서로 다른 조각 크기를 바로 더하는 공식', '분모가 6이 아닌 분수식의 정확한 도해'], keywords: ['분수', '덧셈', '3/6', '2/6', '5/6', '막대', '통분'], dimensions: { width: 1920, height: 840 } },
  'saju-chart-palja': { description: "네 기둥(시·일·월·연)의 천간·지지와 십성, 신살을 한 카드에 담은 사주 앱의 명식 차트. 표본 명식(윤석규)으로 그린 것이다.", subject: '사주', topic: "명식 카드 · 네 기둥", concepts: ["명식", "네 기둥", "십성"], recommendedUses: ['사주 상품 템플릿의 차트 자리 표시', '책에 들어갈 차트의 모양 미리 보기'], avoidUses: ['독자 본인의 명식이나 해석 결과로 보여 주기', '다른 사람의 사주 근거로 쓰기'], keywords: ["사주", "차트", "palja", "명식", "네 기둥", "십성"], dimensions: { width: SAJU_CHART_IMAGES['palja'].width, height: SAJU_CHART_IMAGES['palja'].height }, format: 'image/webp', provenance: { kind: 'imported', source: '사주 앱 차트 컴포넌트 캡처 · 표본 명식(윤석규), saju-react export-studio-chart-images.py', date: '2026-09-25' } },
  'saju-chart-relations': { description: "명식 안의 합·충·형·파와 신살을 기둥별로 정리한 사주 앱의 관계표. 표본 명식(윤석규)으로 그린 것이다.", subject: '사주', topic: "합충 카드 · 내 명식만", concepts: ["합충", "신살", "관계"], recommendedUses: ['사주 상품 템플릿의 차트 자리 표시', '책에 들어갈 차트의 모양 미리 보기'], avoidUses: ['독자 본인의 명식이나 해석 결과로 보여 주기', '다른 사람의 사주 근거로 쓰기'], keywords: ["사주", "차트", "relations", "합충", "신살", "관계"], dimensions: { width: SAJU_CHART_IMAGES['relations'].width, height: SAJU_CHART_IMAGES['relations'].height }, format: 'image/webp', provenance: { kind: 'imported', source: '사주 앱 차트 컴포넌트 캡처 · 표본 명식(윤석규), saju-react export-studio-chart-images.py', date: '2026-09-25' } },
  'saju-chart-daeun': { description: "열 개 대운 구간의 간지·십성과 평생 대운 흐름 그래프, 현재 구간 표시가 있는 사주 앱 차트. 표본 명식(윤석규)으로 그린 것이다.", subject: '사주', topic: "대운 카드 · 열 구간", concepts: ["대운", "흐름 그래프"], recommendedUses: ['사주 상품 템플릿의 차트 자리 표시', '책에 들어갈 차트의 모양 미리 보기'], avoidUses: ['독자 본인의 명식이나 해석 결과로 보여 주기', '다른 사람의 사주 근거로 쓰기'], keywords: ["사주", "차트", "daeun", "대운", "흐름 그래프"], dimensions: { width: SAJU_CHART_IMAGES['daeun'].width, height: SAJU_CHART_IMAGES['daeun'].height }, format: 'image/webp', provenance: { kind: 'imported', source: '사주 앱 차트 컴포넌트 캡처 · 표본 명식(윤석규), saju-react export-studio-chart-images.py', date: '2026-09-25' } },
  'saju-chart-seun': { description: "올해를 가운데 둔 11년 세운의 간지·합충과 세운 흐름 그래프가 있는 사주 앱 차트. 표본 명식(윤석규)으로 그린 것이다.", subject: '사주', topic: "세운 카드 · 앞뒤 11년", concepts: ["세운", "흐름 그래프"], recommendedUses: ['사주 상품 템플릿의 차트 자리 표시', '책에 들어갈 차트의 모양 미리 보기'], avoidUses: ['독자 본인의 명식이나 해석 결과로 보여 주기', '다른 사람의 사주 근거로 쓰기'], keywords: ["사주", "차트", "seun", "세운", "흐름 그래프"], dimensions: { width: SAJU_CHART_IMAGES['seun'].width, height: SAJU_CHART_IMAGES['seun'].height }, format: 'image/webp', provenance: { kind: 'imported', source: '사주 앱 차트 컴포넌트 캡처 · 표본 명식(윤석규), saju-react export-studio-chart-images.py', date: '2026-09-25' } },
  'saju-chart-seun-century': { description: "100년 세운 점수를 대운 구간과 함께 보여 주는 사주 앱의 100년 흐름 차트. 표본 명식(윤석규)으로 그린 것이다.", subject: '사주', topic: "세운 카드 · 100년 지도", concepts: ["세운", "100년", "흐름"], recommendedUses: ['사주 상품 템플릿의 차트 자리 표시', '책에 들어갈 차트의 모양 미리 보기'], avoidUses: ['독자 본인의 명식이나 해석 결과로 보여 주기', '다른 사람의 사주 근거로 쓰기'], keywords: ["사주", "차트", "seun-century", "세운", "100년", "흐름"], dimensions: { width: SAJU_CHART_IMAGES['seun-century'].width, height: SAJU_CHART_IMAGES['seun-century'].height }, format: 'image/webp', provenance: { kind: 'imported', source: '사주 앱 차트 컴포넌트 캡처 · 표본 명식(윤석규), saju-react export-studio-chart-images.py', date: '2026-09-25' } },
  'saju-chart-wolun': { description: "입춘 기준 12개월 월운의 간지·합충과 월운 흐름 그래프가 있는 사주 앱 차트. 표본 명식(윤석규)으로 그린 것이다.", subject: '사주', topic: "월운 카드 · 절기 12개월", concepts: ["월운", "절기", "흐름 그래프"], recommendedUses: ['사주 상품 템플릿의 차트 자리 표시', '책에 들어갈 차트의 모양 미리 보기'], avoidUses: ['독자 본인의 명식이나 해석 결과로 보여 주기', '다른 사람의 사주 근거로 쓰기'], keywords: ["사주", "차트", "wolun", "월운", "절기", "흐름 그래프"], dimensions: { width: SAJU_CHART_IMAGES['wolun'].width, height: SAJU_CHART_IMAGES['wolun'].height }, format: 'image/webp', provenance: { kind: 'imported', source: '사주 앱 차트 컴포넌트 캡처 · 표본 명식(윤석규), saju-react export-studio-chart-images.py', date: '2026-09-25' } },
};

export const CONTENT_ASSETS = Object.freeze([
  { id: 'coins', label: '축적 · 동전', file: 'wealth-coins.png', role: 'icon' },
  { id: 'chart', label: '가치 · 차트', file: 'wealth-chart.png', role: 'icon' },
  { id: 'book', label: '이해 · 책', file: 'wealth-book.png', role: 'icon' },
  { id: 'shield', label: '기준 · 방패', file: 'wealth-shield-check.png', role: 'icon' },
  { id: 'wallet', label: '결과 · 지갑', file: 'wealth-wallet.png', role: 'icon' },
  { id: 'repeat', label: '반복 · 순환', file: 'wealth-repeat.png', role: 'icon' },
  { id: 'shield-alert', label: '확인 · 방패', file: 'wealth-shield-alert.png', role: 'icon' },
  { id: 'eye', label: '관찰 · 눈', file: 'personality-eye.png', role: 'icon' },
  { id: 'sprout', label: '성장 · 새싹', file: 'personality-sprout.png', role: 'icon' },
  { id: 'target', label: '목표 · 과녁', file: 'personality-target.png', role: 'icon' },
  { id: 'thought', label: '생각 · 구름', file: 'personality-thought.png', role: 'icon' },
  { id: 'mountain', label: '과정 · 산', file: 'personality-mountain.png', role: 'icon' },
  { id: 'heart-lock', label: '관계 · 마음', file: 'personality-heart-lock.png', role: 'icon' },
  { id: 'wealth-vignette', label: '재물 흐름 장면', file: 'wealth-footer-vignette.png', role: 'scene' },
  { id: 'study-scene', label: '배움의 장면', file: 'study-exam.png', role: 'scene' },
  { id: 'ivory-hanji', label: '아이보리 한지 질감', file: 'ivory-hanji.webp', role: 'texture' },
  { id: 'learning-paper-grid', label: '학습지 격자 배경', file: 'learning-paper-grid.webp', role: 'background' },
  { id: 'learning-card-frame', label: '학습 설명 카드 프레임', file: 'learning-card-frame.webp', role: 'frame' },
  { id: 'learning-section-plaque', label: '학습 소제목 프레임', file: 'learning-section-plaque.webp', role: 'frame' },
  { id: 'learning-summary-frame', label: '학습 요약 프레임', file: 'learning-summary-frame.webp', role: 'frame' },
  { id: 'saju-page-background', label: '사주 별자리 페이지 배경', file: 'native-page-background-v12.png', role: 'background' },
  { id: 'personality-card-frame', label: '성향 설명 카드 프레임', file: 'personality-card-tall-v3.png', role: 'frame' },
  { id: 'personality-section-frame', label: '성향 소제목 프레임', file: 'personality-section-plaque-v3.png', role: 'frame' },
  { id: 'personality-summary-frame', label: '성향 요약 프레임', file: 'personality-summary-frame-v3.png', role: 'frame' },
  { id: 'wealth-card-frame', label: '재물운 설명 카드 프레임', file: 'wealth-card-tall-v3.png', role: 'frame' },
  { id: 'wealth-section-frame', label: '재물운 소제목 프레임', file: 'wealth-section-plaque-v3.png', role: 'frame' },
  { id: 'wealth-summary-frame', label: '재물운 요약 프레임', file: 'wealth-summary-frame-v3.png', role: 'frame' },
  { id: 'fraction-tiles-hands', label: '손으로 맞추는 분수 타일', file: 'fraction-tiles-hands.webp', role: 'scene' },
  { id: 'fraction-six-equal-parts', label: '여섯 등분 중 한 조각', file: 'fraction-six-equal-parts.webp', role: 'diagram' },
  { id: 'fraction-three-of-six', label: '여섯 등분 중 세 조각', file: 'fraction-three-of-six.webp', role: 'diagram' },
  { id: 'fraction-half-three-sixths', label: '1/2과 3/6은 같은 양', file: 'fraction-half-three-sixths.webp', role: 'diagram' },
  { id: 'fraction-three-plus-two-sixths', label: '3/6과 2/6을 더하기', file: 'fraction-three-plus-two-sixths.webp', role: 'diagram' },
  { id: 'saju-chart-palja', label: "명식 카드 · 네 기둥 (표본)", file: SAJU_CHART_IMAGES['palja'].file, role: 'diagram' },
  { id: 'saju-chart-relations', label: "합충 카드 · 내 명식만 (표본)", file: SAJU_CHART_IMAGES['relations'].file, role: 'diagram' },
  { id: 'saju-chart-daeun', label: "대운 카드 · 열 구간 (표본)", file: SAJU_CHART_IMAGES['daeun'].file, role: 'diagram' },
  { id: 'saju-chart-seun', label: "세운 카드 · 앞뒤 11년 (표본)", file: SAJU_CHART_IMAGES['seun'].file, role: 'diagram' },
  { id: 'saju-chart-seun-century', label: "세운 카드 · 100년 지도 (표본)", file: SAJU_CHART_IMAGES['seun-century'].file, role: 'diagram' },
  { id: 'saju-chart-wolun', label: "월운 카드 · 절기 12개월 (표본)", file: SAJU_CHART_IMAGES['wolun'].file, role: 'diagram' },
].map((asset) => {
  const details = assetDetails[asset.id];
  const isCharacter = ['eye', 'sprout', 'target', 'thought', 'mountain', 'heart-lock'].includes(asset.id);
  return Object.freeze({
    ...asset, src: `${ASSET_ROOT}/${asset.file}`, title: asset.label,
    subject: '사주', recommendedUses: isCharacter ? characterUse : wealthUse,
    avoidUses: isCharacter ? characterAvoid : wealthAvoid,
    dimensions: { width: 256, height: 256 }, format: 'image/png',
    provenance: { kind: 'project', source: 'EduSense 편집 템플릿 에셋' },
    ...details,
    ...(asset.id.startsWith('learning-') ? { provenance: { kind: 'authored', source: 'EduSense 학습용 프레임·배경', date: '2026-09-23' } } : {}),
    ...(asset.id.startsWith('fraction-') ? { format: 'image/webp', provenance: asset.id === 'fraction-tiles-hands'
      ? details.provenance : { kind: 'authored', source: 'EduSense 수학 도해 (정확한 등분)', date: '2026-09-23' } } : {}),
  });
}));

/** Search uses the same catalog that editorial documents and the editor use. */
export function searchContentAssets({ query = '', subject = '', assets = CONTENT_ASSETS } = {}) {
  const terms = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  const group = subject.trim().toLocaleLowerCase();
  return assets.filter(asset => {
    if (group && asset.subject.toLocaleLowerCase() !== group) return false;
    const haystack = [asset.id, asset.title, asset.description, asset.subject, asset.topic,
      ...asset.concepts, ...asset.recommendedUses, ...asset.keywords,
      asset.provenance.kind, asset.provenance.source, asset.format].join(' ').toLocaleLowerCase();
    return terms.every(term => haystack.includes(term));
  });
}

export const CONTENT_TEMPLATE_PRESETS = Object.freeze([
  Object.freeze({
    id: 'saju-editorial', label: '사주 편집',
    description: '네이비와 금장, 의미별 그림과 아이보리 프레임으로 읽는 사주 해설',
    examples: Object.freeze([{ id: 'wealth-flow', label: '재물운 · 개인화 편집 샘플' }, ...SAJU_PRODUCT_EXAMPLES,
      ...EDITOR_SAMPLE_PRESETS.filter(sample => sample.family === 'saju-editorial')]),
    assets: Object.freeze({ paper: 'native-page-background-v12.png', card: 'wealth-card-tall-v3.png',
      plaque: 'wealth-section-plaque-v3.png', summary: 'wealth-summary-frame-v3.png' }),
  }),
  Object.freeze({
    id: 'learning-editorial', label: '학습 편집',
    description: '따뜻한 종이 위에 개념, 이해 과정, 실천을 이어가는 학습 자료',
    examples: Object.freeze([
      { id: 'fraction-addition', label: '분모가 다른 분수의 덧셈 · 수업 예시' },
      { id: 'learning-flow', label: '설명하고 확인하는 공부 · 구성 예시' },
      ...EDITOR_SAMPLE_PRESETS.filter(sample => sample.family === 'learning-editorial'),
    ]),
    assets: Object.freeze({ paper: 'learning-paper-grid.webp', card: 'learning-card-frame.webp',
      plaque: 'learning-section-plaque.webp', summary: 'learning-summary-frame.webp' }),
  }),
]);

const templateIds = new Set(CONTENT_TEMPLATE_PRESETS.map(({ id }) => id));
const assetIds = new Set(CONTENT_ASSETS.map(({ id }) => id));
const sectionKinds = new Set(['cards', 'text', 'steps', 'quote', 'image', 'exercise']);
const itemKinds = new Set(['cards', 'steps', 'exercise']);
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

export class ContentDocumentError extends Error {
  constructor(issues) {
    super(issues.map(({ path, message }) => `${path}: ${message}`).join('\n'));
    this.name = 'ContentDocumentError';
    this.issues = issues;
  }
}

export const WORKSPACE_DOCUMENT_LIMITS = Object.freeze({
  // The longest Saju sample book (century-seun) is 139 pages.
  sections: 160, title: 200, jsonBytes: 64 * 1024 * 1024,
  assetBytes: 4 * 1024 * 1024, totalAssetBytes: 40 * 1024 * 1024,
  sectionAssetBytes: 8 * 1024 * 1024,
  outputDesigns: 16, outputEdits: 2048, outputValueChars: 16000,
});

export function createWorkspaceDocument() {
  return { schemaVersion: 2, format: 'blocksuite', title: '', sections: [] };
}

/**
 * `documentRules: false` leaves out the rules across a document's pages (components.mjs validateDocumentComponents) — for a
 * change the Studio makes page by page and checks whole when it is done (workspace.mjs asOneChange).
 */
export function validateContentDocument(document, { documentRules = true } = {}) {
  if (isObject(document) && [2, 3].includes(document.schemaVersion)) return validateWorkspaceDocument(document, documentRules);
  return validateEditorialDocument(document);
}

function validateEditorialDocument(document) {
  const issues = [];
  const issue = (path, message) => issues.push({ path, message });
  const string = (value, path) => { if (typeof value !== 'string') issue(path, '문자열이어야 합니다.'); };
  const keys = (value, allowed, path) => {
    for (const key of Object.keys(value)) {
      if (!allowed.includes(key)) issue(`${path}.${key}`, '지원하지 않는 필드입니다.');
    }
  };
  const asset = (id, path) => { if (!assetIds.has(id)) issue(path, '등록된 이미지 에셋을 선택해 주세요.'); };
  if (!isObject(document)) return { valid: false, issues: [{ path: '$', message: '콘텐츠 문서는 객체여야 합니다.' }] };
  keys(document, ['schemaVersion', 'templateId', 'title', 'subtitle', 'kicker', 'footer', 'sections'], '$');
  if (document.schemaVersion !== 1) issue('schemaVersion', '지원하는 문서 버전은 1입니다.');
  if (!templateIds.has(document.templateId)) issue('templateId', '등록된 템플릿을 선택해 주세요.');
  for (const field of ['title', 'subtitle', 'kicker', 'footer']) string(document[field], field);
  if (!Array.isArray(document.sections)) issue('sections', '섹션 배열이어야 합니다.');
  else {
    const ids = new Set();
    Array.from(document.sections).forEach((section, index) => {
      const path = `sections[${index}]`;
      if (!isObject(section)) { issue(path, '섹션은 객체여야 합니다.'); return; }
      keys(section, ['id', 'kind', 'title', 'body', 'items', 'assetId', 'alt'], path);
      if (typeof section.id !== 'string' || !/^[a-zA-Z0-9_-]{1,80}$/.test(section.id)) issue(`${path}.id`, '영문, 숫자, 밑줄, 하이픈으로 된 1–80자 ID가 필요합니다.');
      else if (ids.has(section.id)) issue(`${path}.id`, '섹션 ID가 중복됩니다.');
      else ids.add(section.id);
      if (!sectionKinds.has(section.kind)) issue(`${path}.kind`, '지원하지 않는 섹션 종류입니다.');
      string(section.title, `${path}.title`);
      for (const field of ['body', 'alt']) if (Object.hasOwn(section, field)) string(section[field], `${path}.${field}`);
      if (Object.hasOwn(section, 'assetId')) asset(section.assetId, `${path}.assetId`);
      if (Object.hasOwn(section, 'items')) {
        if (!itemKinds.has(section.kind)) issue(`${path}.items`, '카드, 순서, 연습 섹션에서만 항목을 사용할 수 있습니다.');
        if (!Array.isArray(section.items)) issue(`${path}.items`, '항목 배열이어야 합니다.');
        else Array.from(section.items).forEach((item, itemIndex) => {
          const itemPath = `${path}.items[${itemIndex}]`;
          if (!isObject(item)) { issue(itemPath, '항목은 객체여야 합니다.'); return; }
          keys(item, ['title', 'body', 'assetId'], itemPath);
          string(item.title, `${itemPath}.title`);
          string(item.body, `${itemPath}.body`);
          if (Object.hasOwn(item, 'assetId')) asset(item.assetId, `${itemPath}.assetId`);
        });
      }
    });
  }
  return { valid: issues.length === 0, issues };
}

function validateWorkspaceDocument(document, documentRules = true) {
  const issues = [];
  const issue = (path, message) => issues.push({ path, message });
  const keys = (value, allowed, path) => {
    for (const key of Object.keys(value)) {
      if (!allowed.includes(key)) issue(`${path}.${key}`, '지원하지 않는 필드입니다.');
    }
  };
  const title = (value, path) => {
    if (typeof value !== 'string' || value.length > WORKSPACE_DOCUMENT_LIMITS.title) {
      issue(path, '제목은 200자 이하의 문자열이어야 합니다.');
    }
  };
  if (Object.hasOwn(document, 'outputDesigns')) {
    validateOutputDesigns(document.outputDesigns, issue, keys);
    if (issues.length) return { valid: false, issues };
  }
  let json;
  try { json = JSON.stringify(document); }
  catch { return { valid: false, issues: [{ path: '$', message: '문서는 순환 참조가 없는 JSON 데이터여야 합니다.' }] }; }
  if (new TextEncoder().encode(json).byteLength > WORKSPACE_DOCUMENT_LIMITS.jsonBytes) {
    return { valid: false, issues: [{ path: '$', message: '문서 JSON은 UTF-8 기준 64 MiB 이하여야 합니다.' }] };
  }
  keys(document, ['schemaVersion', 'format', 'title', 'sections', 'outputDesigns'], '$');
  const mixed = document.schemaVersion === 3;
  if (document.format !== (mixed ? 'mixed' : 'blocksuite')) issue('format', `작업공간 형식은 ${mixed ? 'mixed' : 'blocksuite'}여야 합니다.`);
  title(document.title, 'title');
  if (!Array.isArray(document.sections)) issue('sections', '섹션 배열이어야 합니다.');
  else if (document.sections.length > WORKSPACE_DOCUMENT_LIMITS.sections) issue('sections', '쪽은 160개 이하로 저장해 주세요.');
  else {
    const sectionIds = new Set();
    let totalAssetBytes = 0;
    Array.from(document.sections).forEach((section, index) => {
      const path = `sections[${index}]`;
      if (!isObject(section)) { issue(path, '섹션은 객체여야 합니다.'); return; }
      const penpot = mixed && section.kind === 'penpot';
      keys(section, [...(penpot ? ['id', 'title', 'kind', 'source', 'preview', 'alt', 'learning'] : ['id', 'title', 'kind', 'snapshot', 'assets', 'origin']), 'components', ...(mixed ? ['alternate'] : [])], path);
      if (Object.hasOwn(section, 'components')) validateSectionComponents(section.components, `${path}.components`, issue);
      if (!penpot && Object.hasOwn(section, 'origin')) validateEditorialOrigin(section.origin, `${path}.origin`, issue, keys);
      if (typeof section.id !== 'string' || !/^[a-zA-Z0-9_-]{1,80}$/.test(section.id)) issue(`${path}.id`, '영문, 숫자, 밑줄, 하이픈으로 된 1–80자 ID가 필요합니다.');
      else if (sectionIds.has(section.id)) issue(`${path}.id`, '섹션 ID가 중복됩니다.');
      else sectionIds.add(section.id);
      title(section.title, `${path}.title`);
      if (mixed && Object.hasOwn(section, 'alternate')) {
        const alternate = section.alternate, alternatePath = `${path}.alternate`;
        if (!isObject(alternate)) issue(alternatePath, '다른 편집기의 원본은 객체여야 합니다.');
        else if (penpot) {
          keys(alternate, ['kind', 'snapshot', 'assets'], alternatePath);
          if (!['document', 'canvas'].includes(alternate.kind)) issue(`${alternatePath}.kind`, 'BlockSuite 원본의 편집 방식이 필요합니다.');
          totalAssetBytes += validateBlockSuiteSection(alternate, alternatePath, issue, keys);
        } else {
          keys(alternate, ['kind', 'source', 'preview', 'alt', 'learning'], alternatePath);
          if (alternate.kind !== 'penpot') issue(`${alternatePath}.kind`, 'Penpot 원본의 편집 방식이 필요합니다.');
          totalAssetBytes += validatePenpotSection(alternate, alternatePath, issue, keys);
        }
      }
      if (penpot) {
        totalAssetBytes += validatePenpotSection(section, path, issue, keys);
        return;
      }
      if (!['document', 'canvas'].includes(section.kind)) issue(`${path}.kind`, '섹션 종류는 document 또는 canvas여야 합니다.');
      totalAssetBytes += validateBlockSuiteSection(section, path, issue, keys);
    });
    if (totalAssetBytes > WORKSPACE_DOCUMENT_LIMITS.totalAssetBytes) issue('sections', '문서의 이미지 원본 합계는 40 MiB 이하여야 합니다.');
    if (!issues.length && documentRules) validateDocumentComponents(document.sections, issue);
  }
  return { valid: issues.length === 0, issues };
}

// Output adapters own the meaning of their edit values. The document owns their
// persistence, so a canvas, saved template and independent copy carry the same
// design without sharing an adapter's pending-edit store.
const unsafeDesignKey = /^(?:__proto__|prototype|constructor)$/i;
const designIdentifier = value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,80}$/.test(value) && !unsafeDesignKey.test(value);

function validateOutputDesigns(designs, issue, keys) {
  let nodes = 0;
  const ancestors = new Set();
  const json = (value, path, depth = 0) => {
    if (++nodes > 100000 || depth > 20) {
      issue(path, '출력 디자인은 100,000개 JSON 값과 20단계 깊이 이하여야 합니다.');
      return false;
    }
    if (value === null || typeof value === 'boolean') return true;
    if (typeof value === 'string' && value.length <= WORKSPACE_DOCUMENT_LIMITS.outputValueChars) return true;
    if (typeof value === 'number' && Number.isFinite(value)) return true;
    const array = Array.isArray(value);
    if (!isObject(value) && !array) {
      issue(path, '출력 디자인 값은 유한한 숫자, 16,000자 이하 문자열 또는 일반 JSON 데이터여야 합니다.');
      return false;
    }
    if (!(array ? Object.getPrototypeOf(value) === Array.prototype : [Object.prototype, null].includes(Object.getPrototypeOf(value))) || ancestors.has(value)) {
      issue(path, '출력 디자인은 순환 참조가 없는 일반 JSON 데이터여야 합니다.');
      return false;
    }
    ancestors.add(value);
    const descriptors = Object.getOwnPropertyDescriptors(value);
    for (const key of Reflect.ownKeys(descriptors)) {
      if (array && key === 'length') continue;
      const descriptor = descriptors[key];
      if (typeof key !== 'string' || unsafeDesignKey.test(key) || !descriptor.enumerable || !Object.hasOwn(descriptor, 'value')
        || (array && !/^(?:0|[1-9][0-9]*)$/.test(key))) {
        issue(path, '출력 디자인에는 일반 JSON 속성만 사용할 수 있습니다.');
        return false;
      }
      if (!json(descriptor.value, `${path}.${key}`, depth + 1)) return false;
    }
    if (array && Object.keys(value).length !== value.length) {
      issue(path, '출력 디자인 배열에는 빈 항목이 없어야 합니다.');
      return false;
    }
    ancestors.delete(value);
    return true;
  };
  if (!json(designs, 'outputDesigns')) return;
  if (!isObject(designs)) { issue('outputDesigns', '출력 디자인은 출력 ID별 객체여야 합니다.'); return; }
  const entries = Object.entries(designs);
  if (entries.length > WORKSPACE_DOCUMENT_LIMITS.outputDesigns) { issue('outputDesigns', '출력 디자인은 16개 이하여야 합니다.'); return; }
  for (const [id, design] of entries) {
    const path = `outputDesigns.${id}`;
    if (!designIdentifier(id)) issue(path, '출력 ID는 영문, 숫자, 밑줄, 하이픈으로 된 1–80자여야 합니다.');
    if (!isObject(design)) { issue(path, '출력 디자인은 객체여야 합니다.'); continue; }
    keys(design, ['edits'], path);
    if (!Array.isArray(design.edits) || design.edits.length > WORKSPACE_DOCUMENT_LIMITS.outputEdits) {
      issue(`${path}.edits`, '출력 디자인 변경은 2,048개 이하의 배열이어야 합니다.');
      continue;
    }
    const addresses = new Set();
    design.edits.forEach((edit, index) => {
      const editPath = `${path}.edits[${index}]`;
      if (!isObject(edit)) { issue(editPath, '출력 디자인 변경은 객체여야 합니다.'); return; }
      keys(edit, ['address', 'base', 'value'], editPath);
      for (const field of ['base', 'value']) if (!Object.hasOwn(edit, field)) issue(`${editPath}.${field}`, '변경 전 값과 변경 후 값이 모두 필요합니다.');
      const parts = typeof edit.address === 'string' ? edit.address.split('/') : [];
      const validAddress = parts[0] === 'layout'
        ? parts.length === 4 && parts.slice(1, 3).every(designIdentifier) && parts[3].split('.').every(designIdentifier)
        : parts[0] === 'picture' && parts.length >= 3 && parts.length <= 8 && parts.slice(1).every(designIdentifier);
      if (!validAddress || edit.address.length > 320) issue(`${editPath}.address`, '출력 디자인 주소는 올바른 layout 또는 picture 경로여야 합니다.');
      else if (addresses.has(edit.address)) issue(`${editPath}.address`, '출력 디자인 변경 주소가 중복됩니다.');
      else addresses.add(edit.address);
    });
  }
}

// Where each text element of a canvas converted from an editorial (v1) document
// came from, e.g. { "kX3…": "product-cover.title" }. Lets a Saju product edited on
// the canvas be read back as product copy (template-saju-document.mjs).
export const EDITORIAL_TEXT_ROLE = /^(?:document\.(?:kicker|title|subtitle|footer)|[a-zA-Z0-9_-]{1,80}\.(?:title|body|items\.\d{1,2}\.(?:title|body)))$/;
const EDITORIAL_ORIGIN_ELEMENTS = 400;

/**
 * The role of one rendered editorial text node (renderer.mjs markup), or null for
 * text that is not document content (step numbers). `node` is what the caller read
 * from the DOM: { tag, classes, sectionId, itemIndex } — itemIndex is the node's
 * card/step position inside its section, when it is inside one.
 */
export function editorialTextRole({ tag, classes = [], sectionId = null, itemIndex = null }) {
  const has = (name) => classes.includes(name);
  if (has('es-content-step-number')) return null;
  if (has('es-content-kicker')) return 'document.kicker';
  if (has('es-content-title')) return 'document.title';
  if (has('es-content-subtitle')) return 'document.subtitle';
  if (has('es-content-footer')) return 'document.footer';
  if (!sectionId) return null;
  if (itemIndex !== null) return tag === 'h3' ? `${sectionId}.items.${itemIndex}.title` : `${sectionId}.items.${itemIndex}.body`;
  if (tag === 'h2') return `${sectionId}.title`;
  return `${sectionId}.body`;
}

function validateEditorialOrigin(origin, path, issue, keys) {
  if (!isObject(origin)) { issue(path, '원본 정보는 객체여야 합니다.'); return; }
  keys(origin, ['templateId', 'sourceTitle', 'elements'], path);
  if (!templateIds.has(origin.templateId)) issue(`${path}.templateId`, '등록된 템플릿이어야 합니다.');
  if (typeof origin.sourceTitle !== 'string' || origin.sourceTitle.length > WORKSPACE_DOCUMENT_LIMITS.title) issue(`${path}.sourceTitle`, '제목은 200자 이하의 문자열이어야 합니다.');
  if (!isObject(origin.elements)) { issue(`${path}.elements`, '요소 대응표는 객체여야 합니다.'); return; }
  const entries = Object.entries(origin.elements);
  if (entries.length > EDITORIAL_ORIGIN_ELEMENTS) issue(`${path}.elements`, `요소 대응표는 ${EDITORIAL_ORIGIN_ELEMENTS}개 이하여야 합니다.`);
  for (const [id, role] of entries) {
    if (!/^[a-zA-Z0-9_-]{1,80}$/.test(id)) issue(`${path}.elements`, '요소 ID 형식이 올바르지 않습니다.');
    else if (typeof role !== 'string' || !EDITORIAL_TEXT_ROLE.test(role)) issue(`${path}.elements.${id}`, '요소 역할 형식이 올바르지 않습니다.');
  }
}

// A picture kept in the Studio's asset store, named by its content hash: the page holds the id, not the bytes (specs/012).
// The store holds at most 1 MiB per picture (StudioAssetController).
export const STORED_ASSET_ID = /^local-[a-f0-9]{64}$/;
export const STORED_ASSET_BYTES = 1024 * 1024;
export const isStoredAsset = asset => isObject(asset) && !Object.hasOwn(asset, 'data') && Object.hasOwn(asset, 'size');

function validateStoredAsset(asset, path, issue) {
  if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(asset.mime)) issue(`${path}.mime`, 'PNG, JPEG, WebP, GIF 이미지만 저장할 수 있습니다.');
  if (Number.isInteger(asset.size) && asset.size >= 1 && asset.size <= STORED_ASSET_BYTES) return asset.size;
  issue(`${path}.size`, '자산 저장소 그림은 1바이트 이상 1 MiB 이하입니다.');
  return 0;
}

function validateBlockSuiteSection(section, path, issue, keys) {
  let totalAssetBytes = 0;
  const savedAssetIds = new Set();
  if (!Array.isArray(section.assets)) issue(`${path}.assets`, '이미지 에셋 배열이어야 합니다.');
  else Array.from(section.assets).forEach((asset, assetIndex) => {
    const assetPath = `${path}.assets[${assetIndex}]`;
    if (!isObject(asset)) { issue(assetPath, '에셋은 객체여야 합니다.'); return; }
    if (isStoredAsset(asset)) {
      keys(asset, ['id', 'mime', 'size'], assetPath);
      if (typeof asset.id !== 'string' || !STORED_ASSET_ID.test(asset.id)) issue(`${assetPath}.id`, '자산 저장소 그림은 local-<sha256> ID로 가리킵니다.');
      else if (savedAssetIds.has(asset.id)) issue(`${assetPath}.id`, '에셋 ID가 중복됩니다.');
      else savedAssetIds.add(asset.id);
      totalAssetBytes += validateStoredAsset(asset, assetPath, issue);
      return;
    }
    keys(asset, ['id', 'mime', 'data'], assetPath);
    if (typeof asset.id !== 'string' || !/^[a-zA-Z0-9_=-]{1,200}$/.test(asset.id)) issue(`${assetPath}.id`, '올바른 이미지 에셋 ID가 필요합니다.');
    else if (savedAssetIds.has(asset.id)) issue(`${assetPath}.id`, '에셋 ID가 중복됩니다.');
    else savedAssetIds.add(asset.id);
    const decodedBytes = validateWorkspaceAsset(asset, assetPath, issue);
    totalAssetBytes += decodedBytes;
  });
  if (totalAssetBytes > WORKSPACE_DOCUMENT_LIMITS.sectionAssetBytes) issue(`${path}.assets`, '한 쪽의 이미지 원본 합계는 8 MiB 이하여야 합니다.');
  validateWorkspaceSnapshot(section.snapshot, `${path}.snapshot`, savedAssetIds, issue);
  return totalAssetBytes;
}

function validatePenpotSection(section, path, issue, keys) {
  if (typeof section.alt !== 'string' || section.alt.length > 2000) issue(`${path}.alt`, '이미지 설명은 2,000자 이하의 문자열이어야 합니다.');
  const { source, preview } = section;
  if (source === null && preview !== null) issue(path, 'Penpot 미리보기에는 원본 연결이 필요합니다.');
  if (source !== null) {
    if (!isObject(source)) issue(`${path}.source`, '원본 연결은 객체 또는 null이어야 합니다.');
    else {
      keys(source, ['origin', 'teamId', 'fileId', 'pageId', 'boardId'], `${path}.source`);
      if (!isCanonicalPenpotOrigin(source.origin)) issue(`${path}.source.origin`, 'Penpot 주소는 경로·인증정보 없는 정규 HTTPS origin 또는 로컬 HTTP origin이어야 합니다.');
      for (const key of ['teamId', 'fileId', 'pageId', ...(source.boardId !== undefined ? ['boardId'] : [])]) {
        if (typeof source[key] !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(source[key])) issue(`${path}.source.${key}`, '완전한 UUID가 필요합니다.');
      }
    }
  }
  if (Object.hasOwn(section, 'learning')) validatePenpotLearning(section, path, issue, keys);
  if (preview === null) return 0;
  if (!isObject(preview)) { issue(`${path}.preview`, 'PNG 미리보기는 객체 또는 null이어야 합니다.'); return 0; }
  keys(preview, ['mime', 'data', 'width', 'height'], `${path}.preview`);
  if (preview.mime !== 'image/png') issue(`${path}.preview.mime`, 'Penpot 미리보기는 PNG여야 합니다.');
  for (const key of ['width', 'height']) {
    if (!Number.isInteger(preview[key]) || preview[key] < 1 || preview[key] > 16384) issue(`${path}.preview.${key}`, '이미지 크기는 1–16,384의 정수여야 합니다.');
  }
  return validateWorkspaceAsset(preview, `${path}.preview`, issue);
}

function validatePenpotLearning(section, path, issue, keys) {
  const learningPath = `${path}.learning`;
  if (!Array.isArray(section.learning) || section.learning.length > 20) {
    issue(learningPath, '학습 상호작용은 20개 이하의 배열이어야 합니다.');
    return;
  }
  if (section.learning.length && !isObject(section.source)) issue(`${path}.source`, '학습 상호작용에는 Penpot 원본 연결이 필요합니다.');
  const ids = new Set();
  const text = (value, valuePath, max, nonblank = true) => {
    if (typeof value !== 'string' || value.length > max || (nonblank && !value.replace(/[\u001c-\u001f]/g, '').trim())) {
      issue(valuePath, `${max.toLocaleString('ko-KR')}자 이하의${nonblank ? ' 비어 있지 않은' : ''} 문자열이어야 합니다.`);
    }
  };
  Array.from(section.learning).forEach((entry, index) => {
    const entryPath = `${learningPath}[${index}]`;
    if (!isObject(entry)) { issue(entryPath, '학습 상호작용은 객체여야 합니다.'); return; }
    const fields = { reveal: ['body'], video: ['url'], quiz: ['question', 'options', 'answerIndex', 'explanation'] };
    const supported = typeof entry.kind === 'string' && Object.hasOwn(fields, entry.kind);
    keys(entry, ['id', 'kind', 'title', 'target', ...(supported ? fields[entry.kind] : [])], entryPath);
    if (!supported) issue(`${entryPath}.kind`, '학습 상호작용은 reveal, video 또는 quiz여야 합니다.');
    if (typeof entry.id !== 'string' || !/^[A-Za-z0-9_-]{1,80}$/.test(entry.id) || ids.has(entry.id)) issue(`${entryPath}.id`, '중복되지 않는 영문·숫자·밑줄·하이픈 1–80자 ID가 필요합니다.');
    else ids.add(entry.id);
    text(entry.title, `${entryPath}.title`, 200);
    if (!isObject(entry.target)) issue(`${entryPath}.target`, 'Penpot 도형 연결 객체가 필요합니다.');
    else {
      const target = entry.target;
      keys(target, ['fileId', 'pageId', 'boardId', 'shapeId', 'bounds'], `${entryPath}.target`);
      for (const key of ['fileId', 'pageId', 'boardId', 'shapeId']) {
        if (typeof target[key] !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(target[key])) issue(`${entryPath}.target.${key}`, '완전한 UUID가 필요합니다.');
        if (key !== 'shapeId' && section.preview !== null && target[key] !== section.source?.[key]) issue(`${entryPath}.target.${key}`, '학습 상호작용은 미리보기의 원본 파일·페이지·보드와 일치해야 합니다.');
      }
      const bounds = target.bounds;
      if (!isObject(bounds)) issue(`${entryPath}.target.bounds`, '보드 내 상대 위치와 크기 객체가 필요합니다.');
      else {
        const boundsPath = `${entryPath}.target.bounds`;
        keys(bounds, ['x', 'y', 'width', 'height'], boundsPath);
        for (const key of ['x', 'y']) if (!Number.isFinite(bounds[key]) || bounds[key] < 0 || bounds[key] >= 1) issue(`${boundsPath}.${key}`, '위치는 0 이상 1 미만의 유한한 숫자여야 합니다.');
        for (const key of ['width', 'height']) if (!Number.isFinite(bounds[key]) || bounds[key] <= 0 || bounds[key] > 1) issue(`${boundsPath}.${key}`, '크기는 0 초과 1 이하의 유한한 숫자여야 합니다.');
        if (Number.isFinite(bounds.x) && Number.isFinite(bounds.width) && bounds.x + bounds.width > 1 + 1e-6) issue(`${boundsPath}.width`, '학습 상호작용은 보드 가로 범위를 벗어날 수 없습니다.');
        if (Number.isFinite(bounds.y) && Number.isFinite(bounds.height) && bounds.y + bounds.height > 1 + 1e-6) issue(`${boundsPath}.height`, '학습 상호작용은 보드 세로 범위를 벗어날 수 없습니다.');
      }
    }
    if (entry.kind === 'reveal') text(entry.body, `${entryPath}.body`, 4000);
    else if (entry.kind === 'video') {
      if (!isCanonicalLearningVideoUrl(entry.url)) issue(`${entryPath}.url`, '영상 주소는 인증정보·공백·프래그먼트 없는 2,048자 이하의 정규 HTTPS 또는 로컬 HTTP 주소여야 합니다.');
    } else if (entry.kind === 'quiz') {
      text(entry.question, `${entryPath}.question`, 2000);
      if (!Array.isArray(entry.options) || entry.options.length < 2 || entry.options.length > 6) issue(`${entryPath}.options`, '선택지는 2–6개 문자열 배열이어야 합니다.');
      else Array.from(entry.options).forEach((option, optionIndex) => text(option, `${entryPath}.options[${optionIndex}]`, 500));
      if (!Number.isInteger(entry.answerIndex) || !Array.isArray(entry.options) || entry.answerIndex < 0 || entry.answerIndex >= entry.options.length) issue(`${entryPath}.answerIndex`, '정답은 선택지 배열 안의 정수 인덱스여야 합니다.');
      text(entry.explanation, `${entryPath}.explanation`, 4000, false);
    }
  });
}

function isCanonicalLearningVideoUrl(value) {
  if (typeof value !== 'string' || value.length > 2048 || /[\s\\\u0000-\u001f\u007f-\u009f#<>"{}|^`]|%(?![a-f0-9]{2})/iu.test(value)) return false;
  try {
    const url = new URL(value);
    const labels = url.hostname.replace(/\.$/, '').split('.');
    const uriHost = url.hostname.startsWith('[') || (labels.every(label => /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(label))
      && (labels.length === 1 || /^[a-z]/i.test(labels.at(-1)) || /^\d+(?:\.\d+){3}$/.test(url.hostname)));
    return url.href === value && !!url.hostname && !url.username && !url.password
      && uriHost && !/[\[\]]/.test(url.pathname)
      && (url.protocol === 'https:' || (url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)));
  } catch { return false; }
}

function isCanonicalPenpotOrigin(value) {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.origin === value && !url.username && !url.password
      && (url.protocol === 'https:' || (url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)));
  } catch { return false; }
}

export function validateWorkspaceAsset(asset, path, issue) {
  const mimeTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
  if (!mimeTypes.includes(asset.mime)) issue(`${path}.mime`, 'PNG, JPEG, WebP, GIF 이미지만 저장할 수 있습니다.');
  const data = asset.data;
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  // A repeated four-character capture exhausts V8's regexp stack on valid multi-MiB
  // images. Validate the alphabet, padding and unused bits without grouped repetition.
  if (typeof data !== 'string' || !data.length || data.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(data)
    || (data.endsWith('==') && (alphabet.indexOf(data.at(-3)) & 15) !== 0)
    || (data.endsWith('=') && !data.endsWith('==') && (alphabet.indexOf(data.at(-2)) & 3) !== 0)) {
    issue(`${path}.data`, '이미지는 공백이 없는 정규 Base64 데이터여야 합니다.');
    return 0;
  }
  const bytes = (data.length / 4) * 3 - (data.endsWith('==') ? 2 : data.endsWith('=') ? 1 : 0);
  if (bytes > WORKSPACE_DOCUMENT_LIMITS.assetBytes) issue(`${path}.data`, '이미지 원본 하나는 4 MiB 이하여야 합니다.');
  const header = atob(data.slice(0, 16));
  const matches = {
    'image/png': header.startsWith('\x89PNG\r\n\x1a\n'),
    'image/jpeg': header.startsWith('\xff\xd8\xff'),
    'image/webp': header.startsWith('RIFF') && header.slice(8, 12) === 'WEBP',
    'image/gif': header.startsWith('GIF87a') || header.startsWith('GIF89a'),
  };
  if (mimeTypes.includes(asset.mime) && !matches[asset.mime]) issue(`${path}.data`, '이미지 파일의 실제 형식이 MIME 형식과 다릅니다.');
  return bytes;
}

const workspaceBlockVersions = Object.freeze({
  'affine:page': 2, 'affine:surface': 5, 'affine:note': 1, 'affine:paragraph': 1,
  'affine:list': 1, 'affine:image': 1, 'affine:divider': 1, 'affine:code': 1,
  'affine:frame': 1, 'affine:edgeless-text': 1, 'affine:latex': 1, 'edusense:component': 1, 'edusense:scene': 1,
});
const unsafeNativeKeys = /^(?:__proto__|prototype|constructor|html|innerHTML|outerHTML|script|srcdoc|iframe|on[a-z]+)$/i;
// Maps keyed by element id — a surface's elements, a frame's members, a group's members (its
// children Y.Map, serialized as children.json): their keys are generated ids, not properties, and an
// id such as "OnYMGPnFKD" is not an event handler.
const elementIdMap = (key, path) => key === 'elements' || key === 'childElementIds' || (key === 'json' && path.endsWith('.children.json'));
const nativeMediaKeys = /^(?:src|source|sourceId)$/i;
const nativeLinkKeys = /^(?:href|url|link)$/i;
const nativeTextKeys = new Set(['insert', 'title', 'caption', 'description', 'text', 'latex']);

function validateWorkspaceSnapshot(snapshot, path, savedAssetIds, issue) {
  if (!isObject(snapshot) || snapshot.type !== 'page' || !isObject(snapshot.meta) || !isObject(snapshot.blocks)) {
    issue(path, 'BlockSuite page 스냅샷의 type, meta, blocks 구조가 필요합니다.');
    return;
  }
  if (typeof snapshot.meta.id !== 'string' || !snapshot.meta.id.length) issue(`${path}.meta.id`, '스냅샷 문서 ID가 필요합니다.');
  if (typeof snapshot.meta.title !== 'string') issue(`${path}.meta.title`, '스냅샷 제목은 문자열이어야 합니다.');
  if (!Number.isFinite(snapshot.meta.createDate) || snapshot.meta.createDate < 0) issue(`${path}.meta.createDate`, '스냅샷 생성 시간이 올바르지 않습니다.');
  if (!Array.isArray(snapshot.meta.tags) || snapshot.meta.tags.some((tag) => typeof tag !== 'string')) issue(`${path}.meta.tags`, '태그는 문자열 배열이어야 합니다.');
  if (snapshot.blocks.flavour !== 'affine:page') issue(`${path}.blocks.flavour`, '스냅샷 루트는 affine:page여야 합니다.');
  if (!Array.isArray(snapshot.blocks.children) || snapshot.blocks.children.filter(block => block?.flavour === 'affine:surface').length !== 1) {
    issue(`${path}.blocks.children`, '문서 루트에는 affine:surface 블록이 정확히 하나 필요합니다.');
  }
  let jsonNodes = 0;
  const walkJSON = (value, valuePath, key = '', depth = 0) => {
    if (valuePath === `${path}.blocks.props.edusenseComponents`) {
      // These records share the native undo transaction. Their calculator source
      // and text baselines are validated as component data, not media references.
      validateSectionComponents(value, valuePath, issue);
      return;
    }
    if (++jsonNodes > 20000 || depth > 64) {
      if (jsonNodes === 20001 || depth === 65) issue(valuePath, '스냅샷은 20,000개 JSON 값과 64단계 깊이 이하여야 합니다.');
      return;
    }
    if (value === null || typeof value === 'boolean') return;
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) issue(valuePath, '숫자는 유한한 값이어야 합니다.');
      return;
    }
    if (typeof value === 'string') {
      if (nativeMediaKeys.test(key) && value && !savedAssetIds.has(value)) issue(valuePath, '이미지 참조는 이 섹션에 저장한 에셋 ID여야 합니다.');
      else if (nativeLinkKeys.test(key) && value && !isSafeWorkspaceLink(value)) issue(valuePath, '링크는 http, https, mailto 주소만 사용할 수 있습니다.');
      else if (!nativeMediaKeys.test(key) && !nativeLinkKeys.test(key) && !nativeTextKeys.has(key)
        && /(?:\b(?:https?|ftp|file|data|blob|javascript|vbscript):|^\s*\/\/|url\s*\()/i.test(value)) {
        issue(valuePath, '검토되지 않은 외부 주소나 CSS 이미지 참조는 저장할 수 없습니다.');
      }
      return;
    }
    if (!Array.isArray(value) && (!isObject(value) || ![Object.prototype, null].includes(Object.getPrototypeOf(value)))) {
      issue(valuePath, '스냅샷은 JSON 값만 포함해야 합니다.');
      return;
    }
    if (Array.isArray(value)) {
      for (let index = 0; index < value.length && jsonNodes <= 20000; index++) walkJSON(value[index], `${valuePath}[${index}]`, key, depth + 1);
      return;
    }
    for (const marker of ['$blocksuite:internal:text$', 'affine:surface:text']) {
      if (!Object.hasOwn(value, marker)) continue;
      if (value[marker] !== true || !Array.isArray(value.delta)
        || value.delta.some((op) => !isObject(op) || typeof op.insert !== 'string' || (Object.hasOwn(op, 'attributes') && !isObject(op.attributes)))) {
        issue(valuePath, '네이티브 텍스트는 문자열 insert를 가진 delta 배열이어야 합니다.');
      }
    }
    for (const [childKey, child] of Object.entries(value)) {
      if (jsonNodes > 20000) break;
      if (!elementIdMap(key, valuePath) && unsafeNativeKeys.test(childKey)) issue(`${valuePath}.${childKey}`, '실행 가능한 HTML 또는 스크립트 속성은 저장할 수 없습니다.');
      if ((nativeLinkKeys.test(childKey) || /^(?:src|sourceId)$/i.test(childKey)) && child !== null && typeof child !== 'string') {
        issue(`${valuePath}.${childKey}`, '주소와 이미지 ID는 문자열이어야 합니다.');
      }
      walkJSON(child, `${valuePath}.${childKey}`, childKey, depth + 1);
    }
  };
  walkJSON(snapshot, path);
  let blockCount = 0;
  const blockIds = new Set();
  const walkBlock = (block, blockPath, depth = 0) => {
    if (++blockCount > 2000 || depth > 32) {
      if (blockCount === 2001 || depth === 33) issue(blockPath, '스냅샷은 2,000개 블록과 32단계 블록 깊이 이하여야 합니다.');
      return;
    }
    if (!isObject(block)) { issue(blockPath, '네이티브 블록은 객체여야 합니다.'); return; }
    if (block.type !== 'block') issue(`${blockPath}.type`, '네이티브 블록의 type은 block이어야 합니다.');
    if (block.flavour === 'affine:surface' && depth !== 1) issue(`${blockPath}.flavour`, '캔버스 블록은 문서 루트의 바로 아래에 있어야 합니다.');
    if (typeof block.id !== 'string' || !block.id.length || block.id.length > 200) issue(`${blockPath}.id`, '네이티브 블록 ID가 올바르지 않습니다.');
    else if (blockIds.has(block.id)) issue(`${blockPath}.id`, '네이티브 블록 ID가 중복됩니다.');
    else blockIds.add(block.id);
    if (!Object.hasOwn(workspaceBlockVersions, block.flavour)) issue(`${blockPath}.flavour`, '지원하지 않는 블록 또는 외부 임베드입니다.');
    else if (block.version !== workspaceBlockVersions[block.flavour]) issue(`${blockPath}.version`, '현재 편집기가 지원하는 네이티브 블록 버전이 아닙니다.');
    if (depth && block.flavour === 'affine:page') issue(`${blockPath}.flavour`, '문서 루트 블록을 중첩할 수 없습니다.');
    if (!isObject(block.props)) issue(`${blockPath}.props`, '블록 속성은 객체여야 합니다.');
    else if (block.flavour === 'affine:image' && !savedAssetIds.has(block.props.sourceId)) issue(`${blockPath}.props.sourceId`, '이미지 원본을 이 섹션의 에셋에 함께 저장해 주세요.');
    else if (block.flavour === 'affine:surface') {
      if (!isObject(block.props.elements)) issue(`${blockPath}.props.elements`, '캔버스 요소는 객체여야 합니다.');
      else for (const [id, element] of Object.entries(block.props.elements)) {
        if (!isObject(element) || !['shape', 'text', 'group', 'connector', 'brush', 'mindmap'].includes(element.type)) {
          issue(`${blockPath}.props.elements.${id}`, '지원하지 않는 캔버스 요소입니다.');
        }
      }
    }
    if (!Array.isArray(block.children)) issue(`${blockPath}.children`, '블록 children은 배열이어야 합니다.');
    else for (let index = 0; index < block.children.length && blockCount <= 2000; index++) walkBlock(block.children[index], `${blockPath}.children[${index}]`, depth + 1);
  };
  walkBlock(snapshot.blocks, `${path}.blocks`);
}

function isSafeWorkspaceLink(value) {
  const address = value.trim();
  if (/[\u0000-\u0020\u007f\\]/.test(address) || !/^(?:https?:\/\/|mailto:)/i.test(address)) return false;
  try {
    const url = new URL(address);
    return ((url.protocol === 'https:' || url.protocol === 'http:') && Boolean(url.hostname))
      || (url.protocol === 'mailto:' && Boolean(url.pathname));
  } catch { return false; }
}

function assertValid(document) {
  const result = validateContentDocument(document);
  if (!result.valid) throw new ContentDocumentError(result.issues);
  return document;
}

/** Ordinary markdown returns null; a broken editorial document is never silently downgraded. */
export function parseContentDocument(markdown) {
  if (typeof markdown !== 'string') throw new ContentDocumentError([{ path: '$', message: '저장된 본문은 문자열이어야 합니다.' }]);
  const blocks = [];
  let fence = null;
  let body = [];
  let hasOutsideContent = false;
  for (const line of markdown.split(/\r?\n/)) {
    if (fence) {
      const close = line.match(/^ {0,3}(`{3,}|~{3,})\s*$/);
      if (close && close[1][0] === fence.marker && close[1].length >= fence.length) {
        if (fence.content) blocks.push(body.join('\n'));
        fence = null;
        body = [];
      } else if (fence.content) body.push(line);
      continue;
    }
    const open = line.match(/^ {0,3}(`{3,}|~{3,})(.*)$/);
    if (!open) {
      if (line.trim()) hasOutsideContent = true;
      continue;
    }
    const info = open[2].trim();
    if (/^edusense-content(?:\s|$)/.test(info) && info !== 'edusense-content') {
      throw new ContentDocumentError([{ path: '$', message: '콘텐츠 코드 블록의 언어는 edusense-content여야 합니다.' }]);
    }
    fence = { marker: open[1][0], length: open[1].length, content: info === 'edusense-content' };
    if (!fence.content) hasOutsideContent = true;
  }
  if (fence?.content) throw new ContentDocumentError([{ path: '$', message: '콘텐츠 코드 블록이 닫히지 않았습니다.' }]);
  if (!blocks.length) return null;
  if (blocks.length > 1) throw new ContentDocumentError([{ path: '$', message: '하나의 본문에는 콘텐츠 문서 하나만 저장할 수 있습니다.' }]);
  if (hasOutsideContent) throw new ContentDocumentError([{ path: '$', message: '콘텐츠 코드 블록 바깥의 본문을 섹션으로 옮겨 주세요. 원문 보존을 위해 혼합 본문은 편집할 수 없습니다.' }]);
  let document;
  try { document = JSON.parse(blocks[0]); }
  catch { throw new ContentDocumentError([{ path: '$', message: '콘텐츠 JSON 형식이 올바르지 않습니다.' }]); }
  return assertValid(document);
}

// The JSON is written without indentation, as the server writes it (EditorialContentDocument.kt): a canvas page nests its
// elements and components ten levels deep, and indenting them doubled a Saju book — the 139-page one to 19 MB, past what a
// save may send (12 MiB at learn.ugot.uk's ingress). Indented documents saved before read the same.
export function serializeContentDocument(document) {
  assertValid(document);
  return `\`\`\`edusense-content\n${JSON.stringify(document)}\n\`\`\``;
}

/** Create an independent EduSense composition from a saved native-source template. */
export function instantiateContentTemplate(document) {
  assertValid(document);
  if (![2, 3].includes(document.schemaVersion)) throw new Error('작업공간 콘텐츠만 템플릿으로 사용할 수 있습니다.');
  const copy = structuredClone(document);
  for (const section of copy.sections) {
    section.id = crypto.randomUUID();
    for (const source of [section, section.alternate]) {
      if (source?.snapshot) source.snapshot.meta.id = crypto.randomUUID();
    }
  }
  assertValid(copy);
  return copy;
}

export function createContentDocument(templateId, options = {}) {
  if (!templateIds.has(templateId)) throw new ContentDocumentError([{ path: 'templateId', message: '등록된 템플릿을 선택해 주세요.' }]);
  const document = { schemaVersion: 1, templateId, title: '', subtitle: '', kicker: '', footer: '', sections: [] };
  if (options.example === undefined) return document;
  const sample = getEditorSample(options.example);
  if (sample && sample.family === templateId) {
    if (sample.editor === 'penpot') return assertValid({ schemaVersion: 3, format: 'mixed', title: sample.title,
      sections: sample.pages.map(page => ({ id: crypto.randomUUID(), kind: 'penpot', title: page.title,
        source: null, preview: null, alt: page.title })) });
    return assertValid({ ...document, ...sample.content });
  }
  if (templateId === 'saju-editorial' && typeof options.example === 'string' && options.example.startsWith(SAJU_PRODUCT_EXAMPLE_PREFIX)) {
    const example = SAJU_PRODUCT_EXAMPLES.find(({ id }) => id === options.example);
    if (!example) throw new ContentDocumentError([{ path: 'example', message: '등록되지 않은 사주 상품 템플릿입니다.' }]);
    return assertValid(createSajuProductDocument(example.productId));
  }
  if (templateId === 'saju-editorial' && options.example === 'wealth-flow') {
    return { ...document, title: '재물운', subtitle: '돈을 만드는 힘과, 내 몫으로 남기는 구조',
      kicker: '윤석규 / 개인화 편집 샘플',
      footer: '전통 명리의 상징 해설 · 사건·성과·확률의 예측이 아닙니다.',
      sections: [
        { id: 'wealth-traits', kind: 'cards', title: '재물 성향', items: [
          { title: '꾸준 축적형', body: '한 번에 크게보다\n작게 검증하며\n쌓아 가는 흐름', assetId: 'coins' },
          { title: '결과 가치형', body: '한 일의 양보다\n만든 결과의\n쓰임을 봅니다', assetId: 'chart' },
          { title: '지식 수익형', body: '기술·정보·콘텐츠를\n쓸모 있는 결과로\n연결합니다', assetId: 'book' },
          { title: '계약 관리형', body: '범위·시간·비용을\n분명히 할 때\n내 몫이 남습니다', assetId: 'shield' },
        ] },
        { id: 'wealth-practice', kind: 'steps', title: '재물 관리의 핵심', items: [
          { title: '약속한 결과', body: '', assetId: 'wallet' },
          { title: '추가되는 일', body: '', assetId: 'repeat' },
          { title: '정산되는 몫', body: '', assetId: 'shield-alert' },
        ] },
        { id: 'wealth-summary', kind: 'quote', title: '특징 · 한 줄 해석',
          body: '바쁘게 일하는 것과 돈이 남는 것은 다릅니다.\n내가 만든 가치만큼, 맡는 범위도 분명해야 합니다.' },
        { id: 'wealth-scene', kind: 'image', title: '', assetId: 'wealth-vignette', alt: '동전, 상승하는 차트, 지갑을 함께 그린 재물 흐름 그림' },
      ] };
  }
  if (templateId === 'learning-editorial' && options.example === 'learning-flow') {
    return { ...document, title: '설명을 내 것으로\n만드는 공부', subtitle: '읽은 내용을 자기 말로 설명하고, 빠진 부분을 확인합니다.',
      kicker: '학습 편집 · 구성 예시', footer: '학습 주제에 맞춰 개념, 사례와 확인 질문을 바꿔 사용하세요.',
      sections: [
        { id: 'learning-cues', kind: 'cards', title: '이해를 확인하는 세 가지 단서', items: [
          { title: '핵심 찾기', body: '이 내용이 답하려는 질문을 한 문장으로 적습니다.', assetId: 'target' },
          { title: '내 말로 설명', body: '자료를 덮고, 핵심 개념과 이유를 연결해 말합니다.', assetId: 'book' },
          { title: '다시 확인', body: '막힌 부분을 원문에서 찾아 설명을 고칩니다.', assetId: 'eye' },
        ] },
        { id: 'learning-why', kind: 'text', title: '설명하다 멈춘 곳이 다음 학습의 출발점',
          body: '뜻을 안다고 느꼈던 단어도 다른 사람에게 설명하려면 더 분명한 표현이 필요합니다. 설명이 이어지지 않는 부분에 표시하고, 원문의 정의나 사례와 비교해 보세요.' },
        { id: 'learning-steps', kind: 'steps', title: '오늘의 학습 순서', items: [
          { title: '질문 정하기', body: '오늘 읽을 내용에서 궁금한 질문 하나를 고릅니다.', assetId: 'thought' },
          { title: '설명 써 보기', body: '핵심 내용과 그 이유를 두 문장으로 연결합니다.', assetId: 'book' },
          { title: '원문과 비교', body: '빠뜨린 조건과 어긋난 표현을 찾아 수정합니다.', assetId: 'eye' },
        ] },
        { id: 'learning-exercise', kind: 'exercise', title: '직접 해 보기',
          body: '지금 배우는 개념 하나를 고르고, 처음 듣는 사람에게 설명해 보세요.', items: [
            { title: '개념', body: '무엇을 뜻하는 말인가요?' },
            { title: '사례', body: '어떤 상황에서 사용할 수 있나요?' },
            { title: '확인', body: '설명을 확인한 자료의 어느 부분이 근거인가요?' },
          ] },
        { id: 'learning-summary', kind: 'quote', title: '오늘 남길 한 문장', body: '잘 설명되는 부분과 다시 확인할 부분을 구분해 보세요.' },
      ] };
  }
  if (templateId === 'learning-editorial' && options.example === 'fraction-addition') {
    return { ...document, title: '분모가 다르면,\n크기부터 맞춰요',
      subtitle: '반 조각과 삼분의 일 조각을 더하려면 어떻게 해야 할까요?',
      kicker: '초등 수학 · 분수의 덧셈', footer: '교육용 편집 예시 · 개념 → 풀이 → 연습 → 확인',
      sections: [
        { id: 'fraction-concepts', kind: 'cards', title: '먼저, 세 가지를 기억해요', items: [
          { title: '분모', body: '한 개를 같은 크기의\n몇 조각으로 나누었는지\n나타내는 수예요.', assetId: 'fraction-six-equal-parts' },
          { title: '분자', body: '나눈 조각 중에서\n몇 조각을 뜻하는지\n나타내는 수예요.', assetId: 'fraction-three-of-six' },
          { title: '통분', body: '분수의 값은 그대로,\n분모를 같게 만드는\n과정이에요.', assetId: 'fraction-half-three-sixths' },
        ] },
        { id: 'fraction-question', kind: 'text', title: '같은 크기의 피자 한 판을 떠올려요',
          body: '피자의 1/2과 1/3을 합치고 싶어요. 하지만 반 조각과 삼분의 일 조각은 크기가 달라요. 조각 수를 바로 더하기 전에, 더 작은 같은 크기의 조각으로 나누어 봅시다.\n\n피자 한 판을 6등분하면 1/2은 3조각, 1/3은 2조각이에요. 합치면 6조각 중 5조각이 됩니다.' },
        { id: 'fraction-hands', kind: 'image', title: '분수 조각을 직접 맞춰 봐요', assetId: 'fraction-tiles-hands',
          alt: '어린이의 양손이 색색의 분수 원 조각을 맞추고, 옆에 노트와 연필이 놓인 활동 장면' },
        { id: 'fraction-process', kind: 'steps', title: '1/2 + 1/3, 이렇게 풀어요', items: [
          { title: '공통 분모 찾기', body: '2와 3의 공배수인 6을 분모로 정해요.', assetId: 'fraction-six-equal-parts' },
          { title: '같은 크기로 바꾸기', body: '1/2 = 3/6, 1/3 = 2/6\n분자와 분모에 같은 수를 곱해요.', assetId: 'fraction-half-three-sixths' },
          { title: '분자끼리 더하기', body: '3/6 + 2/6 = 5/6\n조각의 크기는 그대로, 개수를 더해요.', assetId: 'fraction-three-plus-two-sixths' },
        ] },
        { id: 'fraction-mistake', kind: 'quote', title: '왜 2/5가 아닐까요?',
          body: '분모까지 더하면 조각의 크기가 달라져요.\n먼저 분모를 같게 만든 다음, 분자끼리 더해요.' },
        { id: 'fraction-practice', kind: 'exercise', title: '이제 직접 풀어 보세요',
          body: '공통 분모를 적고, 분자와 분모에 어떤 수를 곱했는지 설명해 보세요.', items: [
            { title: '문제 1 · 1/3 + 1/4', body: '3과 4의 공배수를 찾아보세요. 두 분수를 같은 분모로 바꾸면 어떻게 되나요?' },
            { title: '문제 2 · 2/3 + 1/6', body: '이미 분모가 6인 분수는 그대로 두어도 돼요. 2/3을 바꾸어 계산해 보세요.' },
          ] },
        { id: 'fraction-answers', kind: 'text', title: '풀이를 확인해요',
          body: '문제 1: 1/3 + 1/4 = 4/12 + 3/12 = 7/12\n3과 4의 공배수인 12로 통분했어요.\n\n문제 2: 2/3 + 1/6 = 4/6 + 1/6 = 5/6\n2/3의 분자와 분모에 각각 2를 곱했어요.' },
        { id: 'fraction-summary', kind: 'quote', title: '오늘의 한 문장',
          body: '같은 크기로 나눈 뒤, 조각 수를 더해요.' },
      ] };
  }
  throw new ContentDocumentError([{ path: 'example', message: '이 템플릿에 등록된 예시를 선택해 주세요.' }]);
}
